import { type FieldApi, FieldRow } from '@/components/form/formHelpers'
import type { LicenseResponseBody } from '@/features/Licenses/api/licenseHooks'
import { useRenewOrgLicense } from '@/features/Licenses/api/licenseHooks'
import { LicenseFormDialog } from '@/features/Licenses/components/LicenseFormDialog'
import {
  buildMutationOptions,
  mapMutationError,
} from '@/features/Licenses/lib/mutationHandlers'
import { Button, Input } from '@chakra-ui/react'
import { useForm } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { type ChangeEvent, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const MS_PER_DAY = 1000 * 60 * 60 * 24

/**
 * Default new expiry: today + 365 days at UTC midnight, returned as a
 * `YYYY-MM-DD` string. Using a fixed-day offset (rather than
 * `getUTCFullYear() + 1`) keeps the default deterministic across leap years
 * and around Feb 29 — "today + 1 calendar year" can differ by a day from
 * "today + 365 days" in those cases.
 */
function todayPlus365Days(): string {
  const now = new Date()
  const todayUtcMidnight = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  )
  const plus365 = todayUtcMidnight + 365 * MS_PER_DAY
  return new Date(plus365).toISOString().slice(0, 10)
}

function inputDateToUtcEpochMs(value: string): number {
  const [y, m, d] = value.split('-').map(Number)
  return Date.UTC(y, m - 1, d)
}

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

type Props = { license: LicenseResponseBody; onSuccess: () => void }

export function RenewForm({ license, onSuccess }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const queryClient = useQueryClient()
  const retryRef = useRef<() => void>(() => {
    // replaced after form initialization
  })

  const mutation = useRenewOrgLicense(
    buildMutationOptions({
      queryClient,
      license,
      onSuccess,
      toast: {
        id: 'renew-license-error',
        title: String(t('licenses.actions.renew.error.title')),
        description: String(t('licenses.actions.renew.error.generic')),
        retry: {
          label: String(t('licenses.actions.renew.error.retry')),
          onClick: () => retryRef.current(),
        },
      },
      inlineStatuses: [400, 502],
    }),
  )

  // The new validity must be later than BOTH the current `license.validityEnd`
  // (so a renewal genuinely extends the license rather than shortening it) AND
  // `now()` (so an EXPIRED license can't be renewed into the past either).
  // For ACTIVE licenses, license.validityEnd > now() so it's the binding
  // constraint; for EXPIRED licenses, now() > license.validityEnd is the
  // binding constraint. Math.max() handles both cases uniformly.
  const minValidEpochMs = Math.max(Date.now(), license.validityEnd)

  const schema = z
    .object({
      validityEnd: z.string().regex(ISO_DATE_REGEX),
    })
    .refine((d) => inputDateToUtcEpochMs(d.validityEnd) > minValidEpochMs, {
      message: String(t('licenses.actions.renew.validation.futureDate')),
      path: ['validityEnd'],
    })

  const form = useForm({
    defaultValues: { validityEnd: todayPlus365Days() },
    validators: { onChange: schema },
    onSubmit: async ({ value }) => {
      await mutation
        .mutateAsync({
          pathParams: { orgId: license.orgId, licenseId: license.id },
          body: { validityEnd: inputDateToUtcEpochMs(value.validityEnd) },
        })
        .catch(() => {
          // handled by onError
        })
    },
  })

  retryRef.current = () => void form.handleSubmit()

  const inDialogError = mapMutationError(mutation, {
    inlineStatuses: [400, 502],
    correlationIdStatuses: [502],
    fallbackDetail: String(t('licenses.actions.renew.error.generic')),
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
    >
      <LicenseFormDialog
        title={String(t('licenses.actions.renew.title'))}
        errorTitle={String(t('licenses.actions.renew.error.title'))}
        correlationIdLabel={String(
          t('licenses.actions.renew.error.correlationId'),
        )}
        inDialogError={inDialogError}
        cancelLabel={String(t('licenses.actions.renew.cancel'))}
        submitButton={
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
          >
            {([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                colorPalette="brand"
                loading={Boolean(isSubmitting) || mutation.isPending}
                disabled={!canSubmit || mutation.isPending}
              >
                {String(t('licenses.actions.renew.submit'))}
              </Button>
            )}
          </form.Subscribe>
        }
      >
        <form.Field name="validityEnd">
          {(field) => (
            <FieldRow
              field={field as FieldApi<unknown>}
              label={String(
                t('licenses.actions.renew.fields.validityEnd.label'),
              )}
              helperText={String(
                t('licenses.actions.renew.fields.validityEnd.helper'),
              )}
            >
              <Input
                type="date"
                value={field.state.value}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                onBlur={field.handleBlur}
              />
            </FieldRow>
          )}
        </form.Field>
      </LicenseFormDialog>
    </form>
  )
}
